import { useBlocker } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Button } from '../../../../components/Button';
import { Popover } from '../../../../components/Popover';
import { anchorFrom, type AnchorRect } from '../../../../lib/anchored-panel';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import styles from './AdminSetup.module.css';

/**
 * 06-qol §4 (and review R17): changes not yet saved are never thrown away without asking. Leaving for another pipeline or
 * template, another Admin page or anywhere else in the app stops and asks, in a small pop-up beside what was clicked
 * (never centred, CLAUDE.md §5); closing the tab gets the browser's own warning.
 *
 * Whether there is anything to lose is read through a ref, so a save that has just finished can move to the saved
 * record straight away (`release`).
 */
export function useLeaveGuard(
  isDirty: boolean,
  /** Where to ask when nothing has focus (a browser that doesn't focus a clicked link, the Back button): the editor's head. */
  fallbackRef: RefObject<HTMLElement | null>,
): {
  readonly guard: ReactNode;
  /** Before a navigation that should not ask (after Save, or Cancel on a new one). */
  readonly release: () => void;
} {
  const dirtyRef = useRef(isDirty);
  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const blocker = useBlocker({
    shouldBlockFn: () => {
      if (!dirtyRef.current) return false;
      // Beside what was used to leave: the item or link clicked, else the editor's head.
      const focused = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
      setAnchor(anchorFrom(focused ?? fallbackRef.current ?? document.body));
      return true;
    },
    enableBeforeUnload: () => dirtyRef.current,
    withResolver: true,
  });
  const isOpen = blocker.status === 'blocked' && anchor !== null;
  const discardRef = useRef<HTMLButtonElement | null>(null);
  useFocusOnOpen(discardRef, isOpen);

  const stay = (): void => {
    setAnchor(null);
    if (blocker.status === 'blocked') blocker.reset();
  };
  const leave = (): void => {
    setAnchor(null);
    if (blocker.status !== 'blocked') return;
    dirtyRef.current = false;
    blocker.proceed();
  };

  return {
    release: () => {
      dirtyRef.current = false;
    },
    guard: (
      <Popover anchor={isOpen ? anchor : null} onClose={stay} width={300} label="Discard changes">
        <div className={styles.ask}>
          <h3 className={styles.askTitle}>Discard your changes?</h3>
          <p className={styles.askBody}>They haven't been saved. Keep editing to save them first.</p>
          <div className={styles.askActions}>
            <Button variant="ghost" size="sm" onClick={stay}>
              Keep editing
            </Button>
            <Button ref={discardRef} variant="dangerSolid" size="sm" onClick={leave}>
              Discard
            </Button>
          </div>
        </div>
      </Popover>
    ),
  };
}
